(ns frontend.view.modals.curtains.tag-curtain.view
  (:require
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.options-management.view :as options-management]
   [reagent.core :as r]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]))

(defonce modal-key :curtain/tag)

(defn ref-multi-value
  [v]
  (->> v
       (mapv (fn [item]
               (if (map? item)
                 (:db/id item)
                 item)))))

(defn normalize-value
  [data-type value]
  (case data-type
    :data.type/ref-multi (ref-multi-value value)
    :data.type/ref-single (if (map? value) (:db/id value) value)
    value))

(defn save!
  [{:keys [entity entity-atom attribute previous-value new-value on-success! on-error!]}]
  (when (and (= new-value previous-value) (fn? on-success!))
    (on-success!))
  (when-not (= new-value previous-value)
    (swap! entity-atom assoc attribute new-value)
    (entity-actions/*->field-update!
     (:db/id entity)
     attribute
     new-value
     (fn [_res]
       (when on-success! (on-success!)))
     (fn [err]
       (when (= new-value (get @entity-atom attribute))
         (swap! entity-atom assoc attribute previous-value))
       (when on-error! (on-error! err))))))

(defn tag-field
  [{:keys [entity entity-atom pipeline-id entity-type attribute field-schema close-modal!]}]
  (let [data-type (:field/data-type field-schema)
        multi? (= data-type :data.type/ref-multi)
        read-only? (not (f-permissions/field-write-access? attribute entity-type pipeline-id))
        raw-value (get @entity-atom attribute)
        value (normalize-value data-type raw-value)
        options (->> (fu/named-value-options field-schema pipeline-id)
                     (mapv (fn [o]
                             {:value (:db/id o)
                              :label (entity/display-string o)})))
        label (or (->> field-schema :field/translations :en)
                  (loc/tr attribute)
                  (when-let [fs (registry/get-field-schema @session/tenant-schema attribute)]
                    (loc/field-name attribute fs)))
        admin? (session/is-admin?)
        can-create-option? (and (:create-on-fly? field-schema)
                                (not read-only?)
                                (f-permissions/field-option-create-access? attribute
                                                                           entity-type
                                                                           pipeline-id))
        pipeline-attribute (pipeline/pipeline-key entity-type)
        open-settings! (fn []
                         (modals/open-modal! options-management/modal-key
                                             {:pipeline-attribute pipeline-attribute
                                              :selected-field     attribute})
                         (close-modal!))]
    [labelled-input/with-actions
     {:tools (concat (clipboard/copy-tool value options)
                     (when admin?
                       [{:icon     :tool-item/settings
                         :on-click (fn [_] (open-settings!))
                         :tooltip  (loc/tr :manage-options-title)}]))}
     [labelled-input/view
      {:editor-type (if multi? :editor.type/multi-select :editor.type/select)
       :data-type   data-type
       :label       label
       :read-only?  read-only?
       :disabled?   read-only?
       :value       value
       :options     options
       :prompt-text-creator-keyword (:prompt-text-creator-keyword field-schema)
       :create!     (when can-create-option?
                      (fn [query]
                        (f-entity/open-creator!
                         (field/create-entity-types field-schema)
                         {:named-value-attribute (:named-value-attribute field-schema)
                          :start-value           query
                          :attribute             attribute
                          :skip-success-msg?     true
                          :creation-pipeline-id  pipeline-id
                          :on-ok!                (fn [{:keys [new-entity-id close-cb!]}]
                                                   (when new-entity-id
                                                     (save! {:entity         entity
                                                             :entity-atom    entity-atom
                                                             :attribute      attribute
                                                             :previous-value value
                                                             :new-value      (if multi?
                                                                               (-> (or value [])
                                                                                   (conj new-entity-id)
                                                                                   (vec))
                                                                               new-entity-id)}))
                                                   (close-cb!))})))
       :on-change   (fn [new-value & _]
                      (save! {:entity         entity
                              :entity-atom    entity-atom
                              :attribute      attribute
                              :previous-value value
                              :new-value      (if multi? (vec new-value) new-value)
                              :on-success!    (fn [])
                              :on-error!      (fn [_])}))}]]))

(defn attributes-view
  [{:keys [entity pipeline-id entity-type entity-atom schemas close-modal!]}]
  [:div.tag-curtain-attributes
   {:class "flex flex-col gap-3"}
   (doall
    (for [[attribute field-schema] schemas
          :let [default-create-tag-text? (cu/new-deal-tag? attribute)
                enriched-schema (cond-> field-schema
                                  default-create-tag-text?
                                  (assoc :prompt-text-creator-keyword :search-create-option-tag))]]
      ^{:key (str "tag-curtain-attribute--" attribute)}
      [tag-field
       {:entity        entity
        :entity-atom   entity-atom
        :pipeline-id   pipeline-id
        :entity-type   entity-type
        :attribute     attribute
        :field-schema  enriched-schema
        :close-modal!  close-modal!}]))])

(defn view
  [{:keys [_shown? _topmost? _close-modal! props]}]
  (let [entity-atom (r/cursor session/entities [(:entity-id props)])]
    (fn [{:keys [shown? topmost? close-modal! props]}]
      (let [{:keys [header-text entity-id pipeline-id entity-type]} props
            entity (get @session/entities entity-id)
            tenant-schema @session/tenant-schema
            attribute-schemas (->> (f-permissions/available-tag-attribute-schemas entity-type
                                                                                  pipeline-id)
                                   (sort-by
                                    (fn [as]
                                      (some->> as
                                               (first)
                                               (attribute/attribute-label tenant-schema)))))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :close-button? true}
         header-text
         [attributes-view
          {:entity       entity
           :entity-type  entity-type
           :entity-atom  entity-atom
           :pipeline-id  pipeline-id
           :schemas      attribute-schemas
           :close-modal! close-modal!}]
         [modal/left-cancel-only close-modal! nil]]))))
